import { useEffect, useId, useState } from "react";
import { searchCities } from "../../lib/marketplaceApi";
import type { CityOption } from "../../lib/marketplaceApi";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";

/** Same folding as the API: accents and case don't matter. */
const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** "Cré" in "Créteil" -> Cré in bold. */
function Highlight({ text, query }: { text: string; query: string }) {
  const i = query ? fold(text).indexOf(fold(query.trim())) : -1;
  if (i < 0 || !query.trim()) return <>{text}</>;
  const n = query.trim().length;
  return (
    <>
      {text.slice(0, i)}
      <b className="font-semibold">{text.slice(i, i + n)}</b>
      {text.slice(i + n)}
    </>
  );
}

interface CityAutocompleteProps {
  /** Selected city; "" = the whole area. */
  value: string;
  onChange: (city: string) => void;
  className?: string;
  inputClassName?: string;
  /** Accessible name when there's no visible label. */
  ariaLabel?: string;
}

/**
 * Location field with suggestions from GET /listings/cities: the cities where
 * companies have published vehicles, matched as the user types.
 */
export default function CityAutocomplete({ value, onChange, className, inputClassName, ariaLabel }: CityAutocompleteProps) {
  const t = useT();
  const listId = useId();
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<CityOption[]>([]);
  const [active, setActive] = useState(0);
  // Value changed from outside (URL, reset): show it
  const [shown, setShown] = useState(value);
  if (shown !== value) {
    setShown(value);
    setText(value);
  }

  // Suggestions, with a short pause while typing
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      searchCities(text === value && value ? "" : text).then(
        (rows) => {
          if (!cancelled) {
            setItems(rows);
            setActive(0);
          }
        },
        () => !cancelled && setItems([]),
      );
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text, open, value]);

  // Options: "All" (every location) first, then the matching cities
  const options: { city: string; label: string; count?: number }[] = [
    { city: "", label: t.common.allAreas },
    ...items.map((c) => ({ city: c.city, label: c.city, count: c.listings })),
  ];

  const choose = (city: string) => {
    setText(city);
    setOpen(false);
    onChange(city);
  };

  // Leaving the field: typed text that matches a suggestion is taken as that city
  const close = () => {
    setOpen(false);
    const typed = text.trim();
    if (!typed) return value && onChange("");
    const match = items.find((c) => fold(c.city) === fold(typed));
    if (match) {
      if (match.city !== value) onChange(match.city);
      setText(match.city);
    } else setText(value);
  };

  return (
    <div className={cn("relative", className)}>
      <input
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        autoComplete="off"
        value={text}
        placeholder={t.common.allAreas}
        onBlur={() => open && close()}
        onFocus={(e) => {
          setOpen(true);
          e.target.select();
        }}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(options.length - 1, i + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
          } else if (e.key === "Enter" && open) {
            e.preventDefault();
            // Enter with text typed: the first matching city, not "All"
            const pick = text.trim() && active === 0 && options[1] ? options[1] : options[active];
            if (pick) choose(pick.city);
          } else if (e.key === "Escape") {
            setText(value);
            setOpen(false);
          }
        }}
        className={inputClassName}
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 m-0 mt-1 max-h-72 list-none overflow-y-auto rounded-md bg-white p-1 shadow-[0_8px_24px_rgba(23,26,32,.16)]"
        >
          {options.map((o, i) => (
            <li
              key={o.city || "all"}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus until chosen
                choose(o.city);
              }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                "flex cursor-pointer items-center justify-between gap-3 rounded px-3 py-2 text-sm text-ink",
                i === active && "bg-surface",
                !o.city && "text-ink-soft",
              )}
            >
              <span>{o.city ? <Highlight text={o.label} query={text === value ? "" : text} /> : o.label}</span>
              {o.count !== undefined && <span className="text-xs text-faint">{t.common.vehiclesCount(o.count)}</span>}
            </li>
          ))}
          {text.trim() && text !== value && items.length === 0 && (
            <li className="px-3 py-2 text-xs text-muted">{t.common.noCity}</li>
          )}
        </ul>
      )}
    </div>
  );
}
